import React from "react";
import { Helmet } from "react-helmet-async";

/* ─── Brand Palette ─── */
const C = {
  fg: "#35635a",
  dark: "#264b44",
  bg: "#f4f8f6",
  gold: "#c19a4b",
  white: "#ffffff",
  text: "#1e293b",
  muted: "#64748b",
  light: "#94a3b8",
  border: "#e2e8f0",
  green: "#22c55e",
  red: "#ef4444",
  blue: "#3b82f6",
};

/* ─── Reusable Styles ─── */
const wrap = { maxWidth: 860, margin: "0 auto", padding: "0 20px" };
const card = { background: C.white, borderRadius: 14, padding: "24px 28px", border: `1px solid ${C.border}`, marginBottom: 20 };
const kpiGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 14, marginBottom: 24 };
const kpiCard = { background: C.white, borderRadius: 12, padding: "20px 18px", border: `1px solid ${C.border}`, textAlign: "center" };
const kpiVal = { fontSize: 28, fontWeight: 800, color: C.dark, lineHeight: 1.1 };
const kpiLabel = { fontSize: 12, color: C.muted, marginTop: 4, lineHeight: 1.3 };
const kpiDelta = (up) => ({ fontSize: 11, fontWeight: 600, color: up ? C.green : C.red, marginTop: 2 });
const secTitle = { fontSize: 20, fontWeight: 700, color: C.dark, marginBottom: 6 };
const secSub = { fontSize: 13, color: C.muted, marginBottom: 18 };
const tbl = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th = { textAlign: "left", padding: "10px 12px", borderBottom: `2px solid ${C.fg}`, color: C.dark, fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.5px" };
const td = { padding: "10px 12px", borderBottom: `1px solid ${C.border}`, color: C.text, fontSize: 13 };
const callout = (borderColor) => ({ background: C.bg, borderLeft: `4px solid ${borderColor}`, borderRadius: "0 10px 10px 0", padding: "16px 20px", marginBottom: 18, fontSize: 13, color: C.text, lineHeight: 1.7 });
const badge = (bg, color) => ({ display: "inline-block", padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: bg, color });
const h3Style = { fontSize: 15, fontWeight: 700, color: C.dark, marginBottom: 8, marginTop: 20 };

/* ─── Bar Chart Component ─── */
const HBar = ({ data, max, unit = "", color = C.fg }) => (
  <div style={{ marginBottom: 16 }}>
    {data.map((d) => (
      <div key={d.label} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <span style={{ width: 120, fontSize: 12, color: C.muted, textAlign: "right", flexShrink: 0 }}>{d.label}</span>
        <div style={{ flex: 1, background: "#e2e8f0", borderRadius: 6, height: 22, overflow: "hidden" }}>
          <div style={{ width: `${Math.min((d.value / max) * 100, 100)}%`, height: "100%", background: color, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: C.white }}>{unit}{d.value.toLocaleString()}</span>
          </div>
        </div>
      </div>
    ))}
  </div>
);

/* ─── Progress Bar ─── */
const Progress = ({ pct, label }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: C.dark }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: C.fg }}>{pct}%</span>
    </div>
    <div style={{ background: "#e2e8f0", borderRadius: 8, height: 14, overflow: "hidden" }}>
      <div style={{ width: `${pct}%`, height: "100%", background: `linear-gradient(90deg, ${C.fg}, ${C.gold})`, borderRadius: 8 }} />
    </div>
  </div>
);

/* ─── Page ─── */
const SLDJulyReport = () => {
  return (
    <div style={{ background: C.bg, minHeight: "100vh", fontFamily: "'Inter', -apple-system, sans-serif" }} data-testid="sld-july-report">
      <Helmet>
        <title>Source Leak Detection — July 2026 SEM & SEO Report</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="description" content="Monthly SEM & SEO performance report for Source Leak Detection, July 2026. Prepared by Optima Growth Agency." />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      </Helmet>

      {/* ─── HEADER ─── */}
      <header style={{ background: `linear-gradient(135deg, ${C.dark}, ${C.fg})`, padding: "40px 0 48px", color: C.white }}>
        <div style={wrap}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 28 }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: C.white, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 18, color: C.dark, flexShrink: 0 }}>SLD</div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "2px", opacity: 0.7 }}>Monthly Performance Report</div>
              <h1 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, margin: "4px 0 0", lineHeight: 1.15 }}>SEM & SEO Report</h1>
              <p style={{ fontSize: 15, opacity: 0.8, marginTop: 4 }}>Source Leak Detection · July 2026</p>
            </div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 28px", fontSize: 12, opacity: 0.7 }}>
            <span>Reporting period: 1–31 July 2026</span>
            <span>Prepared by: Optima Growth Agency</span>
            <span>Market: Pretoria / Centurion</span>
          </div>
        </div>
      </header>

      <main style={{ ...wrap, padding: "32px 20px 60px" }}>

        {/* ═══ SECTION 1: EXECUTIVE SUMMARY ═══ */}
        <section style={{ marginBottom: 40 }} data-testid="exec-summary">
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <span style={{ background: C.fg, color: C.white, width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700 }}>1</span>
            <h2 style={secTitle}>Executive Summary</h2>
          </div>
          <p style={secSub}>July 2026 — Campaign Performance Overview</p>

          <p style={{ fontSize: 14, color: C.text, lineHeight: 1.75, marginBottom: 20 }}>
            July was a strong growth month across organic and paid search. Average Google position lifted ~10 places and the brand term reclaimed top of page one; the paid programme scaled lead volume 42% month-on-month. The new custom Next.js website reached 80% completion with automation live, and the social footprint expanded to five active channels.
          </p>

          <div style={kpiGrid}>
            <div style={kpiCard}><div style={kpiVal}>95</div><div style={kpiDelta(true)}>&#9650; 42% vs June</div><div style={kpiLabel}>Paid Conversions</div></div>
            <div style={kpiCard}><div style={kpiVal}>145</div><div style={kpiLabel}>Website Key Events</div><div style={{ fontSize: 11, color: C.muted }}>GA4 web leads</div></div>
            <div style={kpiCard}><div style={kpiVal}>31.2</div><div style={kpiDelta(true)}>&#9650; from 41.4</div><div style={kpiLabel}>Avg. Organic Position</div></div>
            <div style={kpiCard}><div style={kpiVal}>1,498</div><div style={kpiLabel}>Website Sessions</div><div style={{ fontSize: 11, color: C.muted }}>1,295 users</div></div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={callout(C.green)}>
              <strong style={{ color: C.green }}>What went well</strong>
              <ul style={{ margin: "8px 0 0", paddingLeft: 18, lineHeight: 1.9 }}>
                <li>Brand term "source leak detection" at position 2.6 / 14.7% CTR</li>
                <li>"Leak detection pretoria" ranked #1 (SEMrush)</li>
                <li>Pool & PMAX campaigns nearly tripled conversions</li>
                <li>Location-page footprint gaining thousands of local impressions</li>
              </ul>
            </div>
            <div style={callout(C.gold)}>
              <strong style={{ color: C.gold }}>Where to focus next</strong>
              <ul style={{ margin: "8px 0 0", paddingLeft: 18, lineHeight: 1.9 }}>
                <li>Add PMAX negative keywords</li>
                <li>Improve organic CTR on high-impression pages</li>
                <li>Finish final 20% of website build</li>
                <li>Convert the 1,017-user remarketing audience</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ═══ SECTION 2: ORGANIC SEARCH ═══ */}
        <section style={{ marginBottom: 40 }} data-testid="organic-search">
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <span style={{ background: C.fg, color: C.white, width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700 }}>2</span>
            <h2 style={secTitle}>Organic Search (SEO)</h2>
          </div>
          <p style={secSub}>Google Search Console + SEMrush · July 2026</p>

          <div style={kpiGrid}>
            <div style={kpiCard}><div style={kpiVal}>18</div><div style={kpiDelta(true)}>&#9650; 260% from 5</div><div style={kpiLabel}>Organic Clicks</div></div>
            <div style={kpiCard}><div style={kpiVal}>12,106</div><div style={kpiDelta(true)}>&#9650; 0.9%</div><div style={kpiLabel}>Impressions</div></div>
            <div style={kpiCard}><div style={kpiVal}>0.15%</div><div style={kpiDelta(true)}>from 0.04%</div><div style={kpiLabel}>Avg CTR</div></div>
            <div style={kpiCard}><div style={kpiVal}>40.6%</div><div style={kpiDelta(true)}>&#9650; 17.4%</div><div style={kpiLabel}>SEMrush Visibility</div></div>
          </div>

          <div style={card}>
            <h3 style={h3Style}>Top Organic Queries</h3>
            <div style={{ overflowX: "auto" }}>
              <table style={tbl}>
                <thead><tr>
                  <th style={th}>Query</th><th style={th}>Clicks</th><th style={th}>Impr</th><th style={th}>CTR</th><th style={th}>Position</th><th style={th}>Prev</th>
                </tr></thead>
                <tbody>
                  <tr><td style={td}><strong>source leak detection</strong> <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${C.fg}22`, color: C.fg }}>brand</span></td><td style={td}>10</td><td style={td}>68</td><td style={{ ...td, color: C.green, fontWeight: 600 }}>14.7%</td><td style={td}>2.6</td><td style={td}>1.6</td></tr>
                  <tr><td style={td}>leak detection centurion</td><td style={td}>1</td><td style={td}>59</td><td style={td}>1.7%</td><td style={td}>29.7</td><td style={td}>46.3</td></tr>
                  <tr><td style={td}>repair quote</td><td style={td}>1</td><td style={td}>1</td><td style={td}>100%</td><td style={td}>4.0</td><td style={{ ...td, color: C.light }}>—</td></tr>
                  <tr><td style={td}>leak detection pretoria</td><td style={td}>0</td><td style={td}>190</td><td style={td}>0%</td><td style={td}>29.9</td><td style={td}>37.1</td></tr>
                  <tr><td style={td}>pool leak detection</td><td style={td}>0</td><td style={td}>309</td><td style={td}>0%</td><td style={td}>53.7</td><td style={td}>64.3</td></tr>
                  <tr><td style={td}>water leak detection</td><td style={td}>0</td><td style={td}>194</td><td style={td}>0%</td><td style={td}>55.7</td><td style={td}>64.4</td></tr>
                </tbody>
              </table>
            </div>
            <p style={{ fontSize: 12, color: C.muted, marginTop: 10, lineHeight: 1.6 }}>Note: large programmatic long-tail of "pool/water leak detection [suburb]" queries (Waterkloof, Silver Lakes, Moreleta Park, Midstream, Faerie Glen, etc.) gaining impressions and positions.</p>
          </div>

          <div style={card}>
            <h3 style={h3Style}>SEMrush Highlights</h3>
            <ul style={{ fontSize: 13, color: C.text, lineHeight: 1.9, paddingLeft: 18 }}>
              <li><strong>"leak detection pretoria"</strong> ranked <span style={{ color: C.green, fontWeight: 700 }}>#1</span> (top positive mover)</li>
              <li>Homepage strongest page at avg position 2.5</li>
              <li>Appearing in Google Local Pack for 2 tracked terms</li>
              <li>"leak detection centurion" slipped to #4</li>
              <li>"leak finder" dropped 30 places out of top 100</li>
            </ul>
          </div>

          <div style={card}>
            <h3 style={h3Style}>Rankings by Device</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
              <div style={{ textAlign: "center", padding: 14, background: C.bg, borderRadius: 10 }}><div style={{ fontSize: 18, fontWeight: 700, color: C.dark }}>9</div><div style={{ fontSize: 11, color: C.muted }}>Mobile clicks</div><div style={{ fontSize: 11, color: C.light }}>5,312 impr · pos 28.3</div></div>
              <div style={{ textAlign: "center", padding: 14, background: C.bg, borderRadius: 10 }}><div style={{ fontSize: 18, fontWeight: 700, color: C.dark }}>9</div><div style={{ fontSize: 11, color: C.muted }}>Desktop clicks</div><div style={{ fontSize: 11, color: C.light }}>5,119 impr · pos 34.9</div></div>
              <div style={{ textAlign: "center", padding: 14, background: C.bg, borderRadius: 10 }}><div style={{ fontSize: 18, fontWeight: 700, color: C.dark }}>0</div><div style={{ fontSize: 11, color: C.muted }}>Tablet clicks</div><div style={{ fontSize: 11, color: C.light }}>1,675 impr · pos 29.5</div></div>
            </div>
            <p style={{ fontSize: 12, color: C.muted, marginTop: 10 }}>98% of impressions from South Africa.</p>
          </div>

          <div style={callout(C.fg)}>
            <strong>Takeaway:</strong> The organic estate is in a pre-breakthrough phase — impressions and positions climbing off the content calendar and location pages, clicks beginning to follow. Continue internal linking, indexing requests and CTR optimisation.
          </div>
        </section>

        {/* ═══ SECTION 3: PAID SEARCH ═══ */}
        <section style={{ marginBottom: 40 }} data-testid="paid-search">
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <span style={{ background: C.fg, color: C.white, width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700 }}>3</span>
            <h2 style={secTitle}>Paid Search (SEM)</h2>
          </div>
          <p style={secSub}>Google Ads · July 2026</p>

          <p style={{ fontSize: 14, color: C.text, lineHeight: 1.75, marginBottom: 20 }}>
            Four campaigns active in July under a Max-Conversions (tCPA) structure — three ongoing, one short test now paused. Total spend <strong>R44,147</strong> &rarr; <strong>95 conversions</strong> at a blended <strong>R465 cost-per-lead</strong>. Lead volume up <strong style={{ color: C.green }}>42% MoM</strong>.
          </p>

          <div style={card}>
            <div style={{ overflowX: "auto" }}>
              <table style={tbl}>
                <thead><tr>
                  <th style={th}>Campaign</th><th style={th}>Impr</th><th style={th}>Conv</th><th style={th}>Cost (R)</th><th style={th}>Cost/conv</th><th style={th}>vs June</th>
                </tr></thead>
                <tbody>
                  <tr><td style={td}>Water Leak Detection <span style={{ fontSize: 10, color: C.muted }}>(Search·tCPA)</span></td><td style={td}>2,472</td><td style={td}>37.0</td><td style={td}>R23,125</td><td style={td}>R625</td><td style={{ ...td, color: C.red }}>&#9660; 12.1</td></tr>
                  <tr><td style={td}>Pool Leak Detection <span style={{ fontSize: 10, color: C.muted }}>(Search·tCPA)</span></td><td style={td}>1,597</td><td style={td}>38.0</td><td style={td}>R13,680</td><td style={td}>R360</td><td style={{ ...td, color: C.green }}>&#9650; 24.0</td></tr>
                  <tr><td style={td}>PMAX – PLD <span style={{ fontSize: 10, color: C.muted }}>(Performance Max)</span></td><td style={td}>42,221</td><td style={td}>17.0</td><td style={td}>R1,819</td><td style={{ ...td, color: C.green, fontWeight: 700 }}>R107</td><td style={{ ...td, color: C.green }}>&#9650; 13.0</td></tr>
                  <tr style={{ background: "#f8fafc" }}><td style={td}>Leak Detection – Pretoria <span style={{ fontSize: 10, color: C.muted }}>(Search·paused)</span></td><td style={td}>272</td><td style={td}>3.0</td><td style={td}>R5,523</td><td style={td}>R1,841</td><td style={{ ...td, color: C.light }}>new test</td></tr>
                  <tr style={{ fontWeight: 700, background: `${C.fg}08` }}><td style={td}>TOTAL (account)</td><td style={td}>46,562</td><td style={td}>95.0</td><td style={td}>R44,147</td><td style={td}>R465</td><td style={{ ...td, color: C.green, fontWeight: 700 }}>&#9650; 42%</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
            <div style={card}>
              <h3 style={{ ...h3Style, marginTop: 0 }}>Cost per Lead (Active Campaigns)</h3>
              <HBar data={[{ label: "PMAX", value: 107 }, { label: "Pool Search", value: 360 }, { label: "Water Search", value: 625 }]} max={700} unit="R" color={C.fg} />
            </div>
            <div style={card}>
              <h3 style={{ ...h3Style, marginTop: 0 }}>Conversions: July vs June</h3>
              <HBar data={[{ label: "July 2026", value: 95 }, { label: "June 2026", value: 67 }]} max={100} color={C.gold} />
            </div>
          </div>

          <div style={callout(C.gold)}>
            <strong>Search-terms insight:</strong> PMAX served 42,152 impressions / 1,369 clicks at R1.33 avg CPC; mostly relevant queries. Recommend excluding competitor-brand/generic plumbing terms: <em style={{ color: C.muted }}>plumblink, alert plumbing, grant plumbing, maverick plumbers, on time plumbing, steyn plumbing, mike roberts plumbing, borehole pump service</em>.
          </div>

          <div style={callout("#94a3b8")}>
            <strong>Billing note:</strong> "Leak Detection – Pretoria" was a July-only search test (8–17 Jul), now paused. VAT R5,656 applied, offset by a Google promotional credit of &minus;R6,356.
          </div>
        </section>

        {/* ═══ SECTION 4: WEBSITE ANALYTICS ═══ */}
        <section style={{ marginBottom: 40 }} data-testid="website-analytics">
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <span style={{ background: C.fg, color: C.white, width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700 }}>4</span>
            <h2 style={secTitle}>Website Analytics</h2>
          </div>
          <p style={secSub}>GA4 · 3–30 July 2026 · "SLD Clicks & Conversion Tracking"</p>

          <p style={{ fontSize: 14, color: C.text, lineHeight: 1.75, marginBottom: 20 }}>
            1,295 users, 1,498 sessions, 145 web key events. <strong>Paid Search is the strongest lead channel.</strong>
          </p>

          <div style={card}>
            <h3 style={{ ...h3Style, marginTop: 0 }}>New Leads by Channel</h3>
            <table style={tbl}>
              <thead><tr><th style={th}>Channel</th><th style={th}>New Leads</th><th style={th}>New Users</th></tr></thead>
              <tbody>
                <tr><td style={td}><strong>Paid Search</strong></td><td style={{ ...td, fontWeight: 700, color: C.fg }}>15</td><td style={td}>259</td></tr>
                <tr><td style={td}>Cross-network</td><td style={td}>14</td><td style={td}>841</td></tr>
                <tr><td style={td}>Direct</td><td style={td}>4</td><td style={td}>107</td></tr>
                <tr><td style={td}>Organic Search</td><td style={td}>1</td><td style={td}>21</td></tr>
                <tr><td style={td}>Organic Social</td><td style={td}>0</td><td style={td}>29</td></tr>
              </tbody>
            </table>
          </div>

          <div style={card}>
            <h3 style={{ ...h3Style, marginTop: 0 }}>Top Cities (Active Users)</h3>
            <HBar data={[{ label: "Pretoria", value: 538 }, { label: "Johannesburg", value: 152 }, { label: "Centurion", value: 113 }, { label: "Sandton", value: 101 }, { label: "Midrand", value: 40 }]} max={600} color={C.fg} />
          </div>

          <div style={callout(C.blue)}>
            <strong>Remarketing opportunity:</strong> "Remarketing 90 Days – PLD – No Converters" audience now holds <strong>1,017 users</strong> — prime retargeting inventory.
          </div>
        </section>

        {/* ═══ SECTION 5: SOCIAL MEDIA ═══ */}
        <section style={{ marginBottom: 40 }} data-testid="social-media">
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <span style={{ background: C.fg, color: C.white, width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700 }}>5</span>
            <h2 style={secTitle}>Social Media</h2>
          </div>
          <p style={secSub}>Footprint expanded from 2 to 5 channels</p>

          <p style={{ fontSize: 14, color: C.text, lineHeight: 1.75, marginBottom: 20 }}>
            New accounts on X (Twitter), LinkedIn and YouTube; refreshed Facebook and Instagram. Social already driving traffic.
          </p>

          <div style={card}>
            <h3 style={{ ...h3Style, marginTop: 0 }}>Social Traffic (Sessions)</h3>
            <HBar data={[{ label: "YouTube", value: 142 }, { label: "Facebook", value: 26 }, { label: "Instagram", value: 6 }]} max={160} color={C.fg} />
            <p style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>YouTube is the 2nd-largest referral source after Google.</p>
          </div>
        </section>

        {/* ═══ SECTION 6: WEBSITE DEVELOPMENT ═══ */}
        <section style={{ marginBottom: 40 }} data-testid="website-dev">
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <span style={{ background: C.fg, color: C.white, width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700 }}>6</span>
            <h2 style={secTitle}>Website Development</h2>
          </div>
          <p style={secSub}>Custom Next.js website on Emergent platform</p>

          <div style={card}>
            <Progress pct={80} label="Website Build Progress" />
            <ul style={{ fontSize: 13, color: C.text, lineHeight: 1.9, paddingLeft: 18, marginTop: 14 }}>
              <li>Custom front-end and service/location page architecture built</li>
              <li>Automated lead-capture and quote workflows live</li>
              <li><strong>Remaining 20%:</strong> final content, conversion-tracking QA and launch</li>
            </ul>
          </div>
        </section>

        {/* ═══ SECTION 7: RECOMMENDATIONS ═══ */}
        <section style={{ marginBottom: 40 }} data-testid="recommendations">
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <span style={{ background: C.gold, color: C.white, width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700 }}>7</span>
            <h2 style={secTitle}>Recommendations for August</h2>
          </div>
          <p style={secSub}>Prioritised action items</p>

          <div style={{ display: "grid", gap: 10 }}>
            {[
              { pri: "P1", text: "Add PMAX negative keywords to filter competitor-brand/generic plumbing terms." },
              { pri: "P1", text: "Optimise Water Leak campaign CPA (highest active cost-per-lead, R625)." },
              { pri: "P1", text: "Lift organic CTR on high-impression pages (titles/meta descriptions)." },
              { pri: "P2", text: "Launch a retargeting layer to the 1,017-user non-converter audience." },
              { pri: "P2", text: "Finish the website build (final 20%) and validate conversion tracking." },
              { pri: "P3", text: "Build a posting cadence on the three new social channels, leaning into YouTube." },
            ].map((r) => (
              <div key={r.text} style={{ ...card, display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 0, padding: "16px 20px" }}>
                <span style={{
                  ...badge(
                    r.pri === "P1" ? `${C.red}18` : r.pri === "P2" ? `${C.gold}22` : `${C.fg}18`,
                    r.pri === "P1" ? C.red : r.pri === "P2" ? C.gold : C.fg
                  ),
                  flexShrink: 0
                }}>{r.pri}</span>
                <span style={{ fontSize: 13, color: C.text, lineHeight: 1.6 }}>{r.text}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Download PDF */}
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <a
            href="/api/reports/sld-july-2026.pdf"
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: "inline-flex", alignItems: "center", gap: 8, background: C.dark, color: C.white, padding: "14px 32px", borderRadius: 10, fontWeight: 600, fontSize: 14, textDecoration: "none", transition: "opacity 0.2s" }}
            data-testid="download-pdf-btn"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Download PDF Report
          </a>
        </div>

      </main>

      {/* ─── FOOTER ─── */}
      <footer style={{ background: C.dark, color: "rgba(255,255,255,0.6)", padding: "32px 0", fontSize: 12 }}>
        <div style={wrap}>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 20, marginBottom: 16 }}>
            <div>
              <div style={{ color: C.white, fontWeight: 700, fontSize: 14, marginBottom: 6 }}>Prepared by Optima Growth Agency</div>
              <div>info@optimagrowth.agency · optimagrowth.agency</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ color: C.white, fontWeight: 600, marginBottom: 6 }}>Source Leak Detection</div>
              <div>072 899 3159 · 012 006 5179</div>
              <div>info@sourceleakdetection.co.za · www.sourceleakdetection.co.za</div>
            </div>
          </div>
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: 14, opacity: 0.5, textAlign: "center" }}>
            This report is confidential and prepared exclusively for Source Leak Detection. &copy; 2026 Optima Growth Agency.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SLDJulyReport;
